import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

import {ResourceType} from 'common/report/constants';
import {insertAtIndex, replaceByIndex} from 'common/util/array';
import {DisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {FILTER_VALUE_ATTRIBUTES, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  getFirstSegmentValue,
  getSegmentValueResourceType,
  isCohortSegment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {Segmentation} from 'common/types/reports/query';
import {MetricType} from 'common/query-builder/query-block/constants';

import {
  Entry,
  isFilterEntry,
  isFormulaEntry,
  isMetricEntry,
  isPropertyEntry,
  isSegmentEntry,
  QueryFilterEntry,
  QuerySegmentEntry,
  QuerySuperEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from '../types';
import {FilterEntryRow} from '../row-types/filter';
import {createEntryRow} from '../row-types/util';
import {replaceFormulaReferences} from '../row-types/formula-entry/utils';

function filterToBreakdown(entry: QueryFilterEntry) {
  //Filter to breakdown
  if (entry.event.resourceType === ResourceType.Cohort || isFirstTimeEventFilter(entry.event as PropertyFilter)) {
    // skip cohorts and first time filters for now
    return undefined;
  } else {
    const event = omit(entry.event, FILTER_VALUE_ATTRIBUTES) as Segmentation;
    if (entry.event.listItemFilters?.length) {
      // for a list of objects breakdown, we set the selected sub-property (listItemGroup) based on the
      // first sub-property selected in the filter (listItemFilters)
      event.listItemGroup = omit(entry.event.listItemFilters[0], FILTER_VALUE_ATTRIBUTES);
      delete event.listItemFilters;
    }
    return {
      ...entry,
      event,
      type: SelectorTypeDefinitions.Segment,
    };
  }
}

export function segmentToFilter(entry: QuerySegmentEntry): QueryFilterEntry {
  if (isCohortSegment(entry.event)) {
    return undefined;
  }
  const firstEvent = (Array.isArray(entry.event) ? entry.event[0] : entry.event) as Partial<PropertyFilter>;
  const event = PropertyFilterFactory.createFromSegment(firstEvent, {displayContext: DisplayContext.Default});
  return {
    ...FilterEntryRow.getNewEntry(),
    event,
  };
}

function propertyToInlineFilter(section: QueryEntrySection, copiedEntry: QuerySuperEntry, index: number) {
  const row = section.getRows(true)[index];
  const filteringCapabilities = row && section.getRowFilteringCapabilities(row);
  const currentEntry = section.helpers.getEntries()[index];

  if (row && filteringCapabilities.canApplyFilter && isPropertyEntry(copiedEntry)) {
    const filter: PropertyFilter = (isSegmentEntry(copiedEntry) ? segmentToFilter(copiedEntry) : copiedEntry)
      .event as PropertyFilter;
    let entry = currentEntry;

    const isInlineFilter = isFirstTimeEventFilter(filter);
    if (isInlineFilter && !filteringCapabilities.canApplyFirstTimeEventFilter) {
      return undefined;
    }

    if (isMetricEntry(currentEntry)) {
      entry = {
        ...currentEntry,
        behavior: {
          ...currentEntry.behavior,
          filters: [...currentEntry.behavior.filters, filter],
        },
      };
    } else {
      entry = {
        ...currentEntry,
        filters: [...currentEntry.filters, filter],
      };
    }
    return entry;
  }
  return undefined;
}

function likeToLike(section: QueryEntrySection, entry: QuerySuperEntry, index: number) {
  const row = createEntryRow(entry.type, {index, entry, section});
  const filteringCapabilities = row && section.getRowFilteringCapabilities(row);
  // special case for inline filter to global filter
  if (
    isFilterEntry(entry) &&
    !filteringCapabilities.canApplyFirstTimeEventFilter &&
    isFirstTimeEventFilter(entry.event as PropertyFilter)
  ) {
    return undefined;
  }

  return entry;
}

function isInvalidOtherMove(section: QueryEntrySection, entry: QuerySuperEntry): boolean {
  const otherOptions: Set<string> = new Set([
    ...section.attr(`other-filter-options`).map((option) => option.value),
    ...section.attr(`other-group-by-options`).map((option) => option.value),
  ]);
  const isInvalidFilter =
    isFilterEntry(entry) &&
    entry.event.resourceType === ResourceType.Other &&
    !otherOptions.has(entry.event.propertyName as string);
  const isInvalidBreakdown =
    isSegmentEntry(entry) &&
    getSegmentValueResourceType(entry.event) === ResourceType.Other &&
    !otherOptions.has(getFirstSegmentValue(entry.event).propertyName as string);

  return isInvalidFilter || isInvalidBreakdown;
}

/** This function generates new entries given a section and a section we are trying to paste in */
export function addEntryFromExternalSection(
  toSection: QueryEntrySection,
  fromSection: QueryEntrySection,
  copiedEntry: Entry,
  index: number,
  nextEntryKey: number,
  /** Whether the original entry is being retained or not. */
  copy = false,
): {
  entries: Entry[];
  entry: Entry;
  index: number;
  modifyExisting: boolean;
} {
  // TODO: Add logic for metric entry once we have dragging control ready in metric entry
  if (isMetricEntry(copiedEntry)) {
    return undefined;
  }

  let entry;
  let modifyExisting = false;

  if (copiedEntry === undefined) {
    return undefined;
  }
  if (isInvalidOtherMove(toSection, copiedEntry)) {
    return undefined;
  } else if (toSection.helpers.canUseRowType(copiedEntry.type)) {
    entry = likeToLike(toSection, copiedEntry, index);
  } else if (isFilterEntry(copiedEntry) && toSection.helpers.canUseRowType(SelectorTypeDefinitions.Segment)) {
    entry = filterToBreakdown(copiedEntry);
  } else if (isSegmentEntry(copiedEntry) && toSection.helpers.canUseRowType(SelectorTypeDefinitions.Filter)) {
    entry = segmentToFilter(copiedEntry);
  } else if (propertyToInlineFilter(toSection, copiedEntry, index)) {
    entry = propertyToInlineFilter(toSection, copiedEntry, index);
    modifyExisting = true;
  } else {
    return undefined;
  }

  if (entry === undefined) {
    return undefined;
  }

  const isSameSection = fromSection === toSection;
  const beforeToEntries = toSection.helpers.getEntries();

  const entries = cloneDeep(
    modifyExisting
      ? replaceByIndex(beforeToEntries, index, entry)
      : insertAtIndex(beforeToEntries, index, {...entry, entryKey: nextEntryKey}),
  );

  const filteredEntries = entries.filter((entry) => {
    const shouldRemove = !copy && isSameSection && entry.entryKey === copiedEntry.entryKey;

    return !shouldRemove;
  });

  tryUpdateFormulaReferences(
    fromSection,
    toSection,
    copiedEntry,
    modifyExisting ? entry.entryKey : nextEntryKey,
    filteredEntries,
  );

  return {
    entries,
    entry,
    index,
    modifyExisting,
  };
}

/**
 * Calculates changes in formula references and updates formula entries.
 */
function tryUpdateFormulaReferences(
  fromSection: QueryEntrySection,
  toSection: QueryEntrySection,
  movedEntry: QuerySuperEntry,
  newEntryKey: number,
  entries: QuerySuperEntry[],
) {
  const shouldUpdateFormulas =
    toSection.attr(`row-label-type`) === RowLabelType.Alpha && toSection.attr(`row-types`).includes(MetricType.Formula);
  if (shouldUpdateFormulas) {
    const referenceUpdates = {};

    const beforeEntries = toSection.getEntries();

    // if we've reordered an entry in this section, we need to first update its label
    if (fromSection === toSection) {
      const fromIndex = beforeEntries.findIndex((target) => target.entryKey === movedEntry.entryKey);
      const newIndex = entries.findIndex((target) => target.entryKey === newEntryKey);

      const fromLabel = toSection.helpers.getRowLabel(fromIndex);
      const toLabel = toSection.helpers.getRowLabel(newIndex);

      referenceUpdates[fromLabel] = toLabel;
    }

    // track any existing entries that changed index as a result of the drag
    entries.forEach((entry, newIndex) => {
      const oldIndex = beforeEntries.findIndex((target) => target.entryKey === entry.entryKey);

      if (oldIndex !== -1 && newIndex !== oldIndex) {
        const oldLabel = toSection.helpers.getRowLabel(oldIndex);
        const newLabel = toSection.helpers.getRowLabel(newIndex);

        referenceUpdates[oldLabel] = newLabel;
      }
    });

    // update formula definitions
    if (Object.keys(referenceUpdates).length) {
      entries.forEach((entry) => {
        if (isFormulaEntry(entry) && typeof entry.event.definition === `string`) {
          entry.event.definition = replaceFormulaReferences(entry.event.definition, referenceUpdates);
        }
      });
    }
  }
}
